import { useState, useCallback, useMemo, useEffect } from "react"
import { Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"
import { useKeyboxRotation } from "@/hooks/useKeyboxRotation"
import { useSpareKeyboxRotation } from "@/hooks/useSpareKeyboxRotation"
import { KeyboxRotationCard } from "@/parts/KeyboxRotationCard/KeyboxRotationCard"
import { RoomKeyInformation } from "@/model/bigQueryInfo"
import { isSpareKeyboxRotationRequired } from "@/model/spareKeybox"

interface KeyboxRotationCardContainerProps {
    cleaningId: string
    cleaningDate: string
    roomKeyInformation?: RoomKeyInformation
    onAllRotated?: () => void
}

export const KeyboxRotationCardContainer = ({
    cleaningId,
    cleaningDate,
    roomKeyInformation,
    onAllRotated
}: KeyboxRotationCardContainerProps) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)
    const handleClick = useCallback(() => {
        setExpanded(prev => !prev)
    }, [])

    // ゲストキーボックスの情報
    const guestKeyInfo = useMemo(() => {
        return {
            installationMethod:
                roomKeyInformation?.guest_key_Installation_method,
            location: roomKeyInformation?.guest_key_location,
            number: roomKeyInformation?.guest_key_number,
            outLocation: roomKeyInformation?.guest_key_out_location
        }
    }, [roomKeyInformation])

    const guestKeyboxRotation = useKeyboxRotation(cleaningId)
    const spareKeyboxRotation = useSpareKeyboxRotation(cleaningId)

    // ゲストキーボックスとスペアキーボックスがすべて変更済みかどうかを判断
    const isAllRotated = useMemo(() => {
        if (guestKeyboxRotation.isLoading || spareKeyboxRotation.isLoading) {
            return false
        }

        const availableKeyboxes = [
            guestKeyboxRotation?.isAvailable ? guestKeyboxRotation : null,
            spareKeyboxRotation?.isAvailable ? spareKeyboxRotation : null
        ].filter(
            (keybox): keybox is NonNullable<typeof keybox> => keybox !== null
        )

        return availableKeyboxes.every(keybox => keybox.isRotated)
    }, [guestKeyboxRotation, spareKeyboxRotation])

    const showGuestKeyboxRotation = useMemo(
        () =>
            !guestKeyboxRotation.isLoading &&
            guestKeyboxRotation.error === undefined &&
            guestKeyboxRotation.isAvailable,
        [guestKeyboxRotation]
    )

    // スペアキーが番号未登録でも、この条件でカードが消えることはない。
    // m2m-checkin が番号未登録を返すのは、確定済みの履歴が無い（1 回目のローテなので canRotate=true）か、
    // 当日に番号未登録のまま確定した（isRotated=true）場合だけ。確定済みの履歴があれば変更前の番号は
    // その変更後の番号（必ず発行される 4 桁）になるため、番号未登録かつ isRotated=false かつ canRotate=false にはならない
    const showSpareKeyboxRotation = useMemo(
        () =>
            !spareKeyboxRotation.isLoading &&
            spareKeyboxRotation.error === undefined &&
            spareKeyboxRotation.isAvailable &&
            (spareKeyboxRotation.isRotated || spareKeyboxRotation.canRotate),
        [spareKeyboxRotation]
    )

    // 清掃開始の前にスペアキーのローテーションを必須とするか。
    // 番号未登録の物件はカードを表示するが、ローテーションを待たずに清掃を開始できる
    const isSpareKeyboxRotationRequiredForStart = useMemo(
        () =>
            showSpareKeyboxRotation &&
            isSpareKeyboxRotationRequired({
                isAvailable: spareKeyboxRotation.isAvailable ?? false,
                isRegistered: spareKeyboxRotation.isRegistered ?? false,
                isRotated: spareKeyboxRotation.isRotated ?? false
            }),
        [showSpareKeyboxRotation, spareKeyboxRotation]
    )

    const showKeyboxRotationCards = useMemo(
        () => showGuestKeyboxRotation || showSpareKeyboxRotation,
        [showGuestKeyboxRotation, showSpareKeyboxRotation]
    )

    useEffect(() => {
        // 2つのキーボックスがどちらもローテーション必要な場合
        if (showGuestKeyboxRotation && isSpareKeyboxRotationRequiredForStart) {
            // すべてのキーボックスが変更済みの場合にコールバックを実行
            if (isAllRotated) {
                onAllRotated?.()
            }
            return
        }
        // ゲストキーボックスのみがローテーション必要な場合
        if (showGuestKeyboxRotation && !isSpareKeyboxRotationRequiredForStart) {
            // ゲストキーボックスのみが変更済みの場合にコールバックを実行
            if (
                guestKeyboxRotation.isAvailable &&
                guestKeyboxRotation.isRotated
            ) {
                onAllRotated?.()
            }
            return
        }
        // スペアキーボックスのみがローテーション必要な場合
        if (!showGuestKeyboxRotation && isSpareKeyboxRotationRequiredForStart) {
            if (
                spareKeyboxRotation.isAvailable &&
                spareKeyboxRotation.isRotated
            ) {
                onAllRotated?.()
            }
            return
        }

        if (
            !showGuestKeyboxRotation &&
            !isSpareKeyboxRotationRequiredForStart
        ) {
            if (
                !guestKeyboxRotation.isLoading &&
                !spareKeyboxRotation.isLoading
            ) {
                // 両方のキーボックスが存在しない場合は何もしない
                onAllRotated?.()
            }
        }
    }, [
        showGuestKeyboxRotation,
        isSpareKeyboxRotationRequiredForStart,
        guestKeyboxRotation,
        spareKeyboxRotation,
        onAllRotated,
        isAllRotated
    ])

    return showKeyboxRotationCards ? (
        <div
            css={css`
                width: 100%;
                padding: 16px 20px;
                background: ${theme.palette.intentions.cleanerBackground.main};
            `}
        >
            {isAllRotated && (
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={handleClick}
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        padding: 16px 20px;
                        cursor: pointer;
                        background: white;
                        border: 1px solid #e0e0e0;
                        border-radius: ${expanded ? "10px 10px 0 0" : "10px"};
                        outline: none;
                        box-shadow: ${theme.shadows[1]};
                    `}
                >
                    <Typography bolder>
                        {t("キーボックス番号変更済み")}
                    </Typography>
                    {expanded ? (
                        <KeyboardArrowUpIcon />
                    ) : (
                        <KeyboardArrowDownIcon />
                    )}
                </button>
            )}
            {((showGuestKeyboxRotation || showSpareKeyboxRotation) &&
            isAllRotated
                ? expanded
                : true) && (
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        width: 100%;
                        padding: 16px 10px;
                        background: white;
                        border: 1px solid #e0e0e0;
                        border-top: ${
                            isAllRotated ? "none" : "1px solid #e0e0e0"
                        };
                        border-radius: ${
                            isAllRotated ? "0 0 10px 10px" : "10px"
                        };
                        box-shadow: ${theme.shadows[1]};
                    `}
                >
                    {!isAllRotated && (
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                width: 100%;
                                padding: 16px 20px;
                            `}
                        >
                            <Typography bolder>
                                {t("キーボックス番号を変更してください")}
                            </Typography>
                        </div>
                    )}
                    {!guestKeyboxRotation.isLoading &&
                        !guestKeyboxRotation.error &&
                        guestKeyboxRotation?.isAvailable && (
                            <KeyboxRotationCard
                                type="guest"
                                cleaningId={cleaningId}
                                cleaningDate={cleaningDate}
                                keyboxNumber={guestKeyboxRotation.keyboxNumber}
                                currentKeyboxNumber={
                                    guestKeyboxRotation.currentKeyboxNumber
                                }
                                isRotated={guestKeyboxRotation.isRotated}
                                installationMethod={
                                    guestKeyInfo.installationMethod
                                }
                                location={guestKeyInfo.location}
                                outLocation={guestKeyInfo.outLocation}
                                hasPhotoUrl={guestKeyboxRotation.hasPhotoUrl}
                                rotate={guestKeyboxRotation.rotate}
                            />
                        )}
                    {showGuestKeyboxRotation && showSpareKeyboxRotation && (
                        <hr
                            css={css`
                                width: 90%;
                                margin: 20px 0;
                                color: ${
                                    theme.palette.intentions.cleanerGray.main
                                };
                                border: 1px solid
                                    ${
                                        theme.palette.intentions.cleanerGray
                                            .light
                                    };
                            `}
                        />
                    )}
                    {/* showSpareKeyboxRotationでisLoading=false, error=undefinedを保証済みのため非nullアサーション使用 */}
                    {showSpareKeyboxRotation && (
                        <KeyboxRotationCard
                            type="spare"
                            cleaningId={cleaningId}
                            cleaningDate={cleaningDate}
                            keyboxNumber={spareKeyboxRotation.keyboxNumber!}
                            currentKeyboxNumber={
                                spareKeyboxRotation.currentKeyboxNumber
                            }
                            isCurrentKeyboxNumberUnregistered={
                                !spareKeyboxRotation.isRegistered
                            }
                            isRotated={spareKeyboxRotation.isRotated!}
                            installationMethod={
                                spareKeyboxRotation.installationMethod
                            }
                            location={spareKeyboxRotation.location}
                            outLocation={spareKeyboxRotation.outLocation}
                            rotate={spareKeyboxRotation.rotate!}
                            hasPhotoUrl={spareKeyboxRotation.hasPhotoUrl}
                        />
                    )}
                </div>
            )}
        </div>
    ) : null
}
